import { z } from "zod";
import {
  ComponentRegistry,
  defineComponent,
  type ComponentCompileContext,
  type ComponentCompileResult,
} from "./component-registry";
import type { JsonObject, Point } from "./document";
import type {
  GraphicEllipse,
  GraphicFill,
  GraphicLine,
  GraphicNode,
  GraphicPolyline,
  GraphicRect,
  GraphicStroke,
  GraphicText,
} from "./graphic-tree";
import { PANEL_FONT_FAMILY } from "./fonts";

const SCREEN_INK = "#080b0b";
const SCREEN_METAL = "#cdd1cc";
const SCREEN_LIGHT = "#f4f4e9";
const PRINT_INK = "#111111";
const FONT_FAMILY = `${PANEL_FONT_FAMILY}, Arial Narrow, Segoe UI, sans-serif`;
const KNOB_START_DEGREES = 120;
const KNOB_SWEEP_DEGREES = 300;
const SELECTOR_START_DEGREES = -150;
const SELECTOR_SWEEP_DEGREES = 120;

const optionSchema = z
  .object({
    id: z.string().min(1),
    label: z.string(),
    legend: z.string(),
  })
  .strict();

const controlPropsSchema = z
  .object({
    defaultValue: z.number().finite().default(0),
    display: z.string().default(""),
    accent: z.string().default(""),
    options: z.array(optionSchema).default([]),
  })
  .strict();

const jackPropsSchema = z
  .object({
    direction: z.enum(["input", "output", "bidirectional"]),
    signalType: z.enum(["audio", "cv", "gate", "clock", "expression", "mixed", "unknown"]),
  })
  .strict();

const ledPropsSchema = z
  .object({
    active: z.boolean().default(false),
    accent: z.string().default("#ef5a47"),
  })
  .strict();

const panelPropsSchema = z
  .object({
    fill: z.string().default("#181c1c"),
    border: z.string().default("#c9cfcb"),
    borderWidth: z.number().nonnegative().default(0.5),
    radius: z.number().nonnegative().default(1),
  })
  .strict();

const logoPropsSchema = z
  .object({
    text: z.string().default("MOOG"),
    colour: z.string().default("#111617"),
    outline: z.boolean().default(false),
  })
  .strict();

type ControlProps = z.infer<typeof controlPropsSchema>;
type JackProps = z.infer<typeof jackPropsSchema>;
type LedProps = z.infer<typeof ledPropsSchema>;
type PanelProps = z.infer<typeof panelPropsSchema>;
type LogoProps = z.infer<typeof logoPropsSchema>;

function clamp(value: number, minimum = 0, maximum = 1) {
  return Math.min(maximum, Math.max(minimum, value));
}

function radians(degrees: number) {
  return (degrees * Math.PI) / 180;
}

function pointOnCircle(cx: number, cy: number, radius: number, angle: number): Point {
  return { x: cx + Math.cos(angle) * radius, y: cy + Math.sin(angle) * radius };
}

function regularPolygon(
  cx: number,
  cy: number,
  radius: number,
  sides: number,
  offset = 0,
): Point[] {
  return Array.from({ length: sides }, (_, index) =>
    pointOnCircle(cx, cy, radius, offset + (index / sides) * Math.PI * 2),
  );
}

function offsetAlongAngle(
  cx: number,
  cy: number,
  angle: number,
  distance: number,
  tangentOffset: number,
): Point {
  const point = pointOnCircle(cx, cy, distance, angle);
  return {
    x: point.x - Math.sin(angle) * tangentOffset,
    y: point.y + Math.cos(angle) * tangentOffset,
  };
}

function flutedGrip(cx: number, cy: number, radius: number, rotation: number, lobes = 8): Point[] {
  const samples = lobes * 6;
  return Array.from({ length: samples }, (_, index) => {
    const angle = -Math.PI / 2 + (index / samples) * Math.PI * 2;
    const flute = 0.925 + Math.cos((angle - rotation) * lobes) * 0.075;
    return pointOnCircle(cx, cy, radius * flute, angle);
  });
}

function pointedLever(cx: number, cy: number, radius: number, angle: number): Point[] {
  return [
    offsetAlongAngle(cx, cy, angle, -radius * 0.28, radius * 0.22),
    offsetAlongAngle(cx, cy, angle, radius * 0.08, radius * 0.34),
    offsetAlongAngle(cx, cy, angle, radius * 0.68, radius * 0.2),
    offsetAlongAngle(cx, cy, angle, radius * 0.9, 0),
    offsetAlongAngle(cx, cy, angle, radius * 0.68, -radius * 0.2),
    offsetAlongAngle(cx, cy, angle, radius * 0.08, -radius * 0.34),
    offsetAlongAngle(cx, cy, angle, -radius * 0.28, -radius * 0.22),
  ];
}

function solid(colour: string, alpha = 1): GraphicFill {
  return { kind: "solid", colour, alpha };
}

function radialMetal(cx: number, cy: number, radius: number): GraphicFill {
  return {
    kind: "radial-gradient",
    centre: { x: cx - radius * 0.26, y: cy - radius * 0.3 },
    radius: radius * 1.45,
    stops: [
      { offset: 0, colour: "#ffffff" },
      { offset: 0.28, colour: "#d9ddd8" },
      { offset: 0.68, colour: "#8f9692" },
      { offset: 1, colour: "#edf0eb" },
    ],
  };
}

function brushedMetal(cx: number, cy: number, radius: number): GraphicFill {
  return {
    kind: "linear-gradient",
    from: { x: cx - radius, y: cy - radius },
    to: { x: cx + radius, y: cy + radius },
    stops: [
      { offset: 0, colour: "#f4f3eb" },
      { offset: 0.18, colour: "#8d9491" },
      { offset: 0.4, colour: "#e4e3dc" },
      { offset: 0.58, colour: "#777e7b" },
      { offset: 0.8, colour: "#f0efe7" },
      { offset: 1, colour: "#6b7270" },
    ],
  };
}

function radialKnob(cx: number, cy: number, radius: number): GraphicFill {
  return {
    kind: "radial-gradient",
    centre: { x: cx - radius * 0.24, y: cy - radius * 0.3 },
    radius: radius * 1.55,
    stops: [
      { offset: 0, colour: "#343938" },
      { offset: 0.35, colour: "#171a1a" },
      { offset: 0.78, colour: "#070909" },
      { offset: 1, colour: "#000000" },
    ],
  };
}

function printStroke(width = 0.45): GraphicStroke {
  return { colour: PRINT_INK, width, cap: "round", join: "round" };
}

function ellipse(
  id: string,
  cx: number,
  cy: number,
  radiusX: number,
  radiusY: number,
  fill?: GraphicFill,
  stroke?: GraphicStroke,
): GraphicEllipse {
  return { type: "ellipse", id, cx, cy, radiusX, radiusY, fill, stroke };
}

function line(id: string, from: Point, to: Point, stroke: GraphicStroke): GraphicLine {
  return { type: "line", id, from, to, stroke };
}

function polyline(
  id: string,
  points: Point[],
  fill?: GraphicFill,
  stroke?: GraphicStroke,
): GraphicPolyline {
  return { type: "polyline", id, points, closed: true, fill, stroke };
}

function componentResult(
  context: ComponentCompileContext<unknown>,
  graphics: GraphicNode[],
  visualBounds = context.frame,
): ComponentCompileResult {
  const { width, height } = context.frame;
  return {
    graphics,
    visualBounds,
    hitShape: {
      type: "rect",
      rect: { x: 0, y: 0, width, height },
    },
  };
}

function compileKnob(context: ComponentCompileContext<ControlProps>): ComponentCompileResult {
  const { width, height } = context.frame;
  const cx = width / 2;
  const cy = height / 2;
  const radius = Math.max(0.8, Math.min(width, height) / 2);
  const angle = radians(
    KNOB_START_DEGREES + clamp(context.props.defaultValue) * KNOB_SWEEP_DEGREES,
  );
  const graphics: GraphicNode[] = [];

  for (let index = 0; index < 11; index += 1) {
    const tickAngle = radians(KNOB_START_DEGREES + (index / 10) * KNOB_SWEEP_DEGREES);
    graphics.push(
      line(
        `${context.node.id}:tick:${index}`,
        pointOnCircle(cx, cy, radius * 0.82, tickAngle),
        pointOnCircle(cx, cy, radius * 0.98, tickAngle),
        context.target === "print"
          ? printStroke(0.35)
          : { colour: "#c8cecb", width: Math.max(0.28, radius * 0.045), cap: "round" },
      ),
    );
  }

  if (context.target === "print") {
    graphics.push(
      ellipse(
        `${context.node.id}:base`,
        cx,
        cy,
        radius * 0.72,
        radius * 0.72,
        undefined,
        printStroke(),
      ),
      ellipse(
        `${context.node.id}:cap`,
        cx,
        cy,
        radius * 0.47,
        radius * 0.47,
        undefined,
        printStroke(),
      ),
      line(
        `${context.node.id}:indicator`,
        pointOnCircle(cx, cy, radius * 0.48, angle),
        pointOnCircle(cx, cy, radius * 0.78, angle),
        printStroke(0.75),
      ),
    );
  } else {
    graphics.push(
      ellipse(
        `${context.node.id}:shadow`,
        cx + radius * 0.06,
        cy + radius * 0.1,
        radius * 0.79,
        radius * 0.79,
        solid("#000000", 0.66),
      ),
      ellipse(`${context.node.id}:skirt`, cx, cy, radius * 0.78, radius * 0.78, solid("#030505"), {
        colour: "#303534",
        width: Math.max(0.32, radius * 0.05),
      }),
      polyline(
        `${context.node.id}:grip`,
        flutedGrip(cx, cy, radius * 0.74, angle),
        radialKnob(cx, cy, radius),
        {
          colour: "#020303",
          width: Math.max(0.34, radius * 0.065),
          join: "round",
        },
      ),
      ellipse(`${context.node.id}:collar`, cx, cy, radius * 0.51, radius * 0.51, solid("#060808"), {
        colour: "#3b403e",
        width: Math.max(0.22, radius * 0.035),
      }),
      line(
        `${context.node.id}:indicator-underlay`,
        pointOnCircle(cx, cy, radius * 0.5, angle),
        pointOnCircle(cx, cy, radius * 0.78, angle),
        { colour: "#020303", width: Math.max(0.78, radius * 0.18), cap: "round" },
      ),
      line(
        `${context.node.id}:indicator`,
        pointOnCircle(cx, cy, radius * 0.53, angle),
        pointOnCircle(cx, cy, radius * 0.76, angle),
        { colour: SCREEN_LIGHT, width: Math.max(0.62, radius * 0.115), cap: "round" },
      ),
      ellipse(
        `${context.node.id}:cap`,
        cx,
        cy,
        radius * 0.46,
        radius * 0.46,
        brushedMetal(cx, cy, radius * 0.46),
        { colour: "#050707", width: Math.max(0.28, radius * 0.045) },
      ),
      line(
        `${context.node.id}:cap-highlight`,
        pointOnCircle(cx, cy, radius * 0.08, angle + Math.PI),
        pointOnCircle(cx, cy, radius * 0.38, angle + Math.PI),
        {
          colour: "#ffffff",
          width: Math.max(0.16, radius * 0.026),
          alpha: 0.2,
          cap: "round",
        },
      ),
    );
  }
  return componentResult(context, graphics);
}

function compileSelector(context: ComponentCompileContext<ControlProps>): ComponentCompileResult {
  const { width, height } = context.frame;
  const cx = width / 2;
  const cy = height / 2;
  const radius = Math.max(0.8, Math.min(width, height) / 2);
  const count = Math.max(2, context.props.options.length);
  const activeIndex = Math.round(clamp(context.props.defaultValue) * (count - 1));
  const angle = radians(
    SELECTOR_START_DEGREES + (activeIndex / Math.max(1, count - 1)) * SELECTOR_SWEEP_DEGREES,
  );
  const leverPoints = pointedLever(cx, cy, radius, angle);
  const graphics: GraphicNode[] = [];

  if (context.target === "print") {
    graphics.push(
      ellipse(
        `${context.node.id}:base`,
        cx,
        cy,
        radius * 0.82,
        radius * 0.82,
        undefined,
        printStroke(),
      ),
      polyline(`${context.node.id}:lever`, leverPoints, undefined, printStroke()),
      line(
        `${context.node.id}:indicator`,
        pointOnCircle(cx, cy, radius * 0.43, angle),
        pointOnCircle(cx, cy, radius * 0.78, angle),
        printStroke(0.72),
      ),
      ellipse(
        `${context.node.id}:cap`,
        cx,
        cy,
        radius * 0.4,
        radius * 0.4,
        undefined,
        printStroke(),
      ),
    );
  } else {
    graphics.push(
      ellipse(
        `${context.node.id}:shadow`,
        cx + radius * 0.06,
        cy + radius * 0.1,
        radius * 0.84,
        radius * 0.84,
        solid("#000000", 0.68),
      ),
      ellipse(
        `${context.node.id}:base`,
        cx,
        cy,
        radius * 0.82,
        radius * 0.82,
        radialKnob(cx, cy, radius),
        { colour: "#303534", width: Math.max(0.32, radius * 0.05) },
      ),
      ellipse(`${context.node.id}:collar`, cx, cy, radius * 0.5, radius * 0.5, solid("#050707"), {
        colour: "#333836",
        width: Math.max(0.22, radius * 0.035),
      }),
      polyline(`${context.node.id}:lever`, leverPoints, radialKnob(cx, cy, radius), {
        colour: "#020404",
        width: Math.max(0.38, radius * 0.07),
        join: "round",
      }),
      line(
        `${context.node.id}:indicator-underlay`,
        pointOnCircle(cx, cy, radius * 0.42, angle),
        pointOnCircle(cx, cy, radius * 0.8, angle),
        { colour: "#020303", width: Math.max(0.82, radius * 0.19), cap: "round" },
      ),
      line(
        `${context.node.id}:indicator`,
        pointOnCircle(cx, cy, radius * 0.45, angle),
        pointOnCircle(cx, cy, radius * 0.78, angle),
        { colour: SCREEN_LIGHT, width: Math.max(0.62, radius * 0.115), cap: "round" },
      ),
      ellipse(
        `${context.node.id}:cap`,
        cx,
        cy,
        radius * 0.4,
        radius * 0.4,
        brushedMetal(cx, cy, radius * 0.4),
        { colour: "#050707", width: Math.max(0.26, radius * 0.04) },
      ),
      line(
        `${context.node.id}:cap-highlight`,
        pointOnCircle(cx, cy, radius * 0.07, angle + Math.PI),
        pointOnCircle(cx, cy, radius * 0.33, angle + Math.PI),
        {
          colour: "#ffffff",
          width: Math.max(0.14, radius * 0.024),
          alpha: 0.18,
          cap: "round",
        },
      ),
    );
  }

  return componentResult(context, graphics);
}

function compileToggle(context: ComponentCompileContext<ControlProps>): ComponentCompileResult {
  const { width, height } = context.frame;
  const cx = width / 2;
  const cy = height / 2 + Math.min(height * 0.12, 1.5);
  const radius = Math.max(1, Math.min(width, height) * 0.22);
  const count = Math.max(2, context.props.options.length);
  const activeIndex = Math.round(clamp(context.props.defaultValue) * (count - 1));
  const travel = Math.min(width * 0.28, radius * 1.15);
  const endX = cx + ((activeIndex / Math.max(1, count - 1)) * 2 - 1) * travel;
  const endY = cy - Math.min(height * 0.31, radius * 1.5);
  const nut = regularPolygon(cx, cy, radius * 1.08, 8, Math.PI / 8);
  const graphics: GraphicNode[] = [
    ellipse(
      `${context.node.id}:shadow`,
      cx + radius * 0.14,
      cy + radius * 0.18,
      radius * 1.06,
      radius * 1.06,
      context.target === "print" ? undefined : solid("#000000", 0.58),
      context.target === "print" ? printStroke(0.25) : undefined,
    ),
    polyline(
      `${context.node.id}:nut`,
      nut,
      context.target === "print" ? undefined : radialMetal(cx, cy, radius * 1.08),
      context.target === "print" ? printStroke() : { colour: "#343938", width: 0.35 },
    ),
    ellipse(
      `${context.node.id}:collar`,
      cx,
      cy,
      radius * 0.67,
      radius * 0.67,
      context.target === "print" ? undefined : solid("#151919"),
      context.target === "print" ? printStroke(0.3) : { colour: "#777e7b", width: 0.3 },
    ),
    line(
      `${context.node.id}:stalk-shadow`,
      { x: cx + radius * 0.12, y: cy },
      { x: endX + radius * 0.12, y: endY + radius * 0.12 },
      context.target === "print"
        ? printStroke(1.05)
        : { colour: "#000000", width: Math.max(1.1, radius * 0.5), alpha: 0.7, cap: "round" },
    ),
    line(
      `${context.node.id}:stalk`,
      { x: cx, y: cy - radius * 0.12 },
      { x: endX, y: endY },
      context.target === "print"
        ? { colour: "#ffffff", width: Math.max(0.65, radius * 0.28), cap: "round" }
        : { colour: SCREEN_METAL, width: Math.max(0.95, radius * 0.4), cap: "round" },
    ),
    ellipse(
      `${context.node.id}:stalk-cap`,
      endX,
      endY,
      radius * 0.28,
      radius * 0.28,
      context.target === "print" ? solid("#ffffff") : radialMetal(endX, endY, radius * 0.28),
      context.target === "print" ? printStroke(0.28) : { colour: "#343938", width: 0.28 },
    ),
  ];
  return componentResult(context, graphics);
}

function compileButton(context: ComponentCompileContext<ControlProps>): ComponentCompileResult {
  const { width, height } = context.frame;
  const active = context.props.defaultValue >= 0.5;
  const accent = context.props.accent || "#d9dd57";
  const lamp = context.variant === "lamp";
  if (lamp) {
    const radius = Math.max(0.8, Math.min(width, height) * 0.38);
    const cx = width / 2;
    const cy = height / 2;
    const graphics: GraphicNode[] = [];
    if (active && context.target === "screen") {
      for (let index = 0; index < 12; index += 1) {
        const angle = (index / 12) * Math.PI * 2;
        graphics.push(
          line(
            `${context.node.id}:ray:${index}`,
            pointOnCircle(cx, cy, radius * 1.1, angle),
            pointOnCircle(cx, cy, radius * 1.65, angle),
            { colour: accent, width: 0.55, alpha: 0.62, cap: "round" },
          ),
        );
      }
    }
    graphics.push(
      ellipse(
        `${context.node.id}:bezel`,
        cx,
        cy,
        radius,
        radius,
        context.target === "print" ? undefined : solid("#141716"),
        context.target === "print" ? printStroke() : { colour: "#b8bfbc", width: 0.4 },
      ),
      ellipse(
        `${context.node.id}:lamp`,
        cx,
        cy,
        radius * 0.72,
        radius * 0.72,
        context.target === "print" ? undefined : solid(active ? accent : "#6c211d"),
        context.target === "print"
          ? printStroke(0.3)
          : { colour: active ? "#fff4cf" : "#2c0c0b", width: 0.3 },
      ),
    );
    return componentResult(context, graphics);
  }

  const radius = context.variant === "candy" ? Math.min(width, height) * 0.14 : 0.7;
  const inset = Math.max(0.7, Math.min(width, height) * 0.12);
  const faceY = inset + (active ? Math.min(0.55, height * 0.05) : 0);
  const graphics: GraphicNode[] = [
    {
      type: "rect",
      id: `${context.node.id}:bezel`,
      x: 0,
      y: 0,
      width,
      height,
      radius,
      fill: context.target === "print" ? undefined : solid("#030505"),
      stroke: context.target === "print" ? printStroke() : { colour: "#69706d", width: 0.5 },
    },
    {
      type: "rect",
      id: `${context.node.id}:face`,
      x: inset,
      y: faceY,
      width: Math.max(0, width - inset * 2),
      height: Math.max(0, height - inset * 2),
      radius: Math.max(0.5, radius * 0.72),
      fill: context.target === "print" ? undefined : solid(active ? accent : "#b9bfba"),
      stroke:
        context.target === "print"
          ? printStroke(0.35)
          : { colour: active ? "#fffbd9" : "#f5f5eb", width: active ? 0.65 : 0.42, alpha: 0.9 },
    },
    {
      type: "path",
      id: `${context.node.id}:lower-edge`,
      data: `M ${inset * 1.5} ${height - inset * 1.45} Q ${width / 2} ${height - inset * 0.5} ${width - inset * 1.5} ${height - inset * 1.45}`,
      stroke:
        context.target === "print"
          ? printStroke(0.3)
          : { colour: "#111414", width: 0.55, alpha: 0.7, cap: "round" },
    },
  ];
  if (context.props.display) {
    graphics.push({
      type: "text",
      id: `${context.node.id}:display`,
      value: context.props.display.toUpperCase(),
      x: width / 2,
      y: -Math.max(1.2, height * 0.23),
      align: "center",
      verticalAlign: "bottom",
      frameMode: "hug",
      overflow: "visible",
      colour: context.target === "print" ? PRINT_INK : "#d9ddda",
      fontFamily: FONT_FAMILY,
      fontSize: Math.max(1.5, Math.min(2.4, height * 0.18)),
      fontWeight: 800,
      letterSpacing: 0.08,
    });
  }
  const displayHeight = context.props.display ? Math.max(1.8, height * 0.28) : 0;
  return componentResult(context, graphics, {
    x: 0,
    y: -displayHeight,
    width,
    height: height + displayHeight,
  });
}

function compileSlider(context: ComponentCompileContext<ControlProps>): ComponentCompileResult {
  const { width, height } = context.frame;
  const cx = width / 2;
  const inset = Math.max(1, height * 0.05);
  const handleHeight = Math.max(2.4, Math.min(height * 0.16, width * 0.58));
  const travel = Math.max(0, height - inset * 2 - handleHeight);
  const handleY = inset + (1 - clamp(context.props.defaultValue)) * travel;
  const graphics: GraphicNode[] = [];
  for (let index = 0; index < 15; index += 1) {
    const y = inset + (index / 14) * (height - inset * 2);
    graphics.push(
      line(
        `${context.node.id}:tick:${index}`,
        { x: width * 0.14, y },
        { x: width * 0.86, y },
        context.target === "print" ? printStroke(0.22) : { colour: "#aeb7b4", width: 0.3 },
      ),
    );
  }
  graphics.push(
    {
      type: "rect",
      id: `${context.node.id}:track`,
      x: cx - Math.max(0.65, width * 0.07),
      y: inset,
      width: Math.max(1.3, width * 0.14),
      height: height - inset * 2,
      fill: context.target === "print" ? undefined : solid("#030404"),
      stroke: context.target === "print" ? printStroke(0.3) : { colour: "#686f6d", width: 0.25 },
    },
    {
      type: "rect",
      id: `${context.node.id}:handle-shadow`,
      x: width * 0.08 + 0.6,
      y: handleY + 0.7,
      width: width * 0.84,
      height: handleHeight,
      radius: 0.5,
      fill: context.target === "print" ? undefined : solid("#000000", 0.6),
      stroke: context.target === "print" ? printStroke(0.2) : undefined,
    },
    {
      type: "rect",
      id: `${context.node.id}:handle`,
      x: width * 0.05,
      y: handleY,
      width: width * 0.84,
      height: handleHeight,
      radius: 0.5,
      fill:
        context.target === "print"
          ? undefined
          : {
              kind: "linear-gradient",
              from: { x: width * 0.05, y: handleY },
              to: { x: width * 0.89, y: handleY + handleHeight },
              stops: [
                { offset: 0, colour: "#e7e9e4" },
                { offset: 0.5, colour: "#707773" },
                { offset: 1, colour: "#d9ddd8" },
              ],
            },
      stroke: context.target === "print" ? printStroke(0.4) : { colour: "#c9ceca", width: 0.38 },
    },
    line(
      `${context.node.id}:handle-line`,
      { x: width * 0.13, y: handleY + handleHeight / 2 },
      { x: width * 0.81, y: handleY + handleHeight / 2 },
      context.target === "print" ? printStroke(0.35) : { colour: "#f1f2eb", width: 0.55 },
    ),
  );
  return componentResult(context, graphics);
}

function compileWheel(context: ComponentCompileContext<ControlProps>): ComponentCompileResult {
  const { width, height } = context.frame;
  const inset = Math.max(0.8, Math.min(width, height) * 0.08);
  const markerRadius = Math.max(0.7, Math.min(width * 0.16, height * 0.045));
  const markerLimit = height / 2 - inset * 1.5 - markerRadius;
  const markerY = height / 2 + markerLimit - clamp(context.props.defaultValue) * markerLimit * 2;
  const graphics: GraphicNode[] = [
    {
      type: "rect",
      id: `${context.node.id}:housing`,
      x: 0,
      y: 0,
      width,
      height,
      radius: 0.6,
      fill: context.target === "print" ? undefined : solid("#030404"),
      stroke: context.target === "print" ? printStroke() : { colour: "#5b6260", width: 0.5 },
    },
    {
      type: "rect",
      id: `${context.node.id}:wheel`,
      x: width * 0.2,
      y: inset,
      width: width * 0.6,
      height: height - inset * 2,
      radius: Math.min(1.3, width * 0.12),
      fill:
        context.target === "print"
          ? undefined
          : {
              kind: "linear-gradient",
              from: { x: width * 0.2, y: 0 },
              to: { x: width * 0.8, y: 0 },
              stops: [
                { offset: 0, colour: "#c4c6bf" },
                { offset: 0.45, colour: "#fffef3" },
                { offset: 1, colour: "#b7bbb5" },
              ],
            },
      stroke: context.target === "print" ? printStroke(0.3) : { colour: "#555a58", width: 0.35 },
    },
  ];
  for (let index = 0; index < 17; index += 1) {
    const y = inset * 1.8 + (index / 16) * (height - inset * 3.6);
    graphics.push(
      line(
        `${context.node.id}:rib:${index}`,
        { x: width * 0.25, y },
        { x: width * 0.75, y },
        context.target === "print"
          ? printStroke(0.15)
          : { colour: "#434846", width: 0.2, alpha: 0.42 },
      ),
    );
  }
  graphics.push(
    ellipse(
      `${context.node.id}:indicator`,
      width / 2,
      markerY,
      markerRadius,
      markerRadius,
      context.target === "print" ? undefined : solid("#8c322b"),
      context.target === "print" ? printStroke(0.32) : { colour: "#eee9df", width: 0.35 },
    ),
  );
  return componentResult(context, graphics);
}

function compileJack(context: ComponentCompileContext<JackProps>): ComponentCompileResult {
  const { width, height } = context.frame;
  const cx = width / 2;
  const cy = height / 2;
  const radius = Math.max(0.7, Math.min(width, height) / 2);
  const nutRadius = radius * 0.84;
  const graphics: GraphicNode[] = [
    polyline(
      `${context.node.id}:nut`,
      regularPolygon(cx, cy, nutRadius, 6, -Math.PI / 2),
      context.target === "print" ? undefined : radialMetal(cx, cy, nutRadius),
      context.target === "print"
        ? printStroke(0.4)
        : { colour: "#eef1eb", width: 0.42, join: "round" },
    ),
    ellipse(
      `${context.node.id}:washer`,
      cx,
      cy,
      radius * 0.58,
      radius * 0.58,
      context.target === "print" ? undefined : solid("#747c78"),
      context.target === "print" ? printStroke(0.3) : { colour: "#1b1f1e", width: 0.28 },
    ),
    ellipse(
      `${context.node.id}:socket`,
      cx,
      cy,
      radius * 0.43,
      radius * 0.43,
      context.target === "print" ? undefined : solid(SCREEN_INK),
      context.target === "print" ? printStroke(0.32) : { colour: "#050707", width: 0.3 },
    ),
    ellipse(
      `${context.node.id}:socket-highlight`,
      cx - radius * 0.12,
      cy - radius * 0.13,
      radius * 0.12,
      radius * 0.12,
      context.target === "print" ? undefined : solid("#ffffff", 0.36),
    ),
  ];
  return {
    ...componentResult(context, graphics),
    anchors: { tip: { x: cx, y: cy } },
    hitShape: { type: "circle", centre: { x: cx, y: cy }, radius },
  };
}

function compileLed(context: ComponentCompileContext<LedProps>): ComponentCompileResult {
  const { width, height } = context.frame;
  const cx = width / 2;
  const cy = height / 2;
  const radius = Math.min(width, height) / 2;
  const graphics: GraphicNode[] = [
    ellipse(
      `${context.node.id}:bezel`,
      cx,
      cy,
      radius,
      radius,
      context.target === "print" ? undefined : solid("#121515"),
      context.target === "print" ? printStroke(0.3) : { colour: "#69706d", width: 0.25 },
    ),
    ellipse(
      `${context.node.id}:light`,
      cx,
      cy,
      radius * 0.62,
      radius * 0.62,
      context.target === "print"
        ? undefined
        : solid(context.props.active ? context.props.accent : "#64231f"),
      context.target === "print" ? printStroke(0.2) : undefined,
    ),
  ];
  return componentResult(context, graphics);
}

function compilePanel(context: ComponentCompileContext<PanelProps>): ComponentCompileResult {
  const graphic: GraphicRect = {
    type: "rect",
    id: `${context.node.id}:panel`,
    x: 0,
    y: 0,
    width: context.frame.width,
    height: context.frame.height,
    radius: context.props.radius,
    fill: context.target === "print" ? undefined : solid(context.props.fill),
    stroke: {
      colour: context.target === "print" ? PRINT_INK : context.props.border,
      width: context.props.borderWidth,
    },
  };
  return componentResult(context, [graphic]);
}

function compileLogo(context: ComponentCompileContext<LogoProps>): ComponentCompileResult {
  if (context.variant === "moog") {
    const sx = context.frame.width / 130;
    const sy = context.frame.height / 48;
    const stroke: GraphicStroke = {
      colour: context.target === "print" ? PRINT_INK : context.props.colour,
      width: Math.max(0.28, Math.min(sx, sy) * 3.1),
      cap: "round",
      join: "round",
    };
    const path = (id: string, data: string): GraphicNode => ({
      type: "path",
      id: `${context.node.id}:${id}`,
      data,
      stroke,
    });
    const oval = (id: string, cx: number): GraphicEllipse =>
      ellipse(`${context.node.id}:${id}`, cx * sx, 23 * sy, 16 * sx, 16 * sy, undefined, stroke);
    return componentResult(context, [
      path(
        "m",
        `M ${1 * sx} ${39 * sy} L ${1 * sx} ${13 * sy} C ${1 * sx} ${5 * sy}, ${15 * sx} ${5 * sy}, ${15 * sx} ${13 * sy} L ${15 * sx} ${39 * sy} M ${15 * sx} ${13 * sy} C ${15 * sx} ${5 * sy}, ${29 * sx} ${5 * sy}, ${29 * sx} ${13 * sy} L ${29 * sx} ${39 * sy}`,
      ),
      oval("first-o", 49),
      oval("second-o", 81),
      line(
        `${context.node.id}:slash`,
        { x: 71 * sx, y: 34 * sy },
        { x: 91 * sx, y: 12 * sy },
        stroke,
      ),
      oval("g-loop", 113),
      path(
        "g-tail",
        `M ${129 * sx} ${7 * sy} L ${129 * sx} ${38 * sy} C ${129 * sx} ${46 * sy}, ${117 * sx} ${48 * sy}, ${108 * sx} ${44 * sy}`,
      ),
    ]);
  }
  const graphic: GraphicText = {
    type: "text",
    id: `${context.node.id}:wordmark`,
    value: context.props.text,
    x: 0,
    y: context.frame.height / 2,
    width: context.frame.width,
    height: context.frame.height,
    align: "left",
    verticalAlign: "middle",
    frameMode: "fixed-box",
    overflow: "visible",
    colour: context.target === "print" ? PRINT_INK : context.props.colour,
    fontFamily: FONT_FAMILY,
    fontSize: context.frame.height * 0.74,
    fontWeight: 800,
    horizontalScale: 0.88,
    stroke: context.props.outline
      ? { colour: context.target === "print" ? PRINT_INK : context.props.colour, width: 0.35 }
      : undefined,
  };
  return componentResult(context, [graphic]);
}

export const CORE_COMPONENT_TYPES = [
  "core.button",
  "core.jack",
  "core.knob",
  "core.led",
  "core.lever",
  "core.logo",
  "core.panel",
  "core.quarter-inch-jack",
  "core.rotary-selector",
  "core.slider",
  "core.toggle",
  "core.trim-knob",
  "core.wheel",
] as const;

export type CoreComponentType = (typeof CORE_COMPONENT_TYPES)[number];
export type CoreComponentCategory =
  "Controls" | "Patch points" | "Indicators" | "Structure" | "Branding";

export type CoreComponentCatalogEntry = {
  type: CoreComponentType;
  label: string;
  category: CoreComponentCategory;
  defaultSize: { width: number; height: number };
  createDefaultProps: () => JsonObject;
  keywords: readonly string[];
};

export function isCoreComponentType(value: string): value is CoreComponentType {
  return (CORE_COMPONENT_TYPES as readonly string[]).includes(value);
}

const controlDefaults = (): JsonObject => ({
  defaultValue: 0,
  display: "",
  accent: "",
  options: [],
});

/**
 * Authoring metadata for reusable hardware primitives. Rendering remains owned by
 * the registry; future placement tools can consume this catalog without knowing
 * anything about Pixi or the editor shell.
 */
export const CORE_COMPONENT_CATALOG: readonly CoreComponentCatalogEntry[] = [
  {
    type: "core.knob",
    label: "Continuous knob",
    category: "Controls",
    defaultSize: { width: 20, height: 20 },
    createDefaultProps: controlDefaults,
    keywords: ["dial", "potentiometer", "control"],
  },
  {
    type: "core.trim-knob",
    label: "Trim knob",
    category: "Controls",
    defaultSize: { width: 14, height: 14 },
    createDefaultProps: controlDefaults,
    keywords: ["dial", "attenuator", "small"],
  },
  {
    type: "core.rotary-selector",
    label: "Rotary selector",
    category: "Controls",
    defaultSize: { width: 20, height: 20 },
    createDefaultProps: controlDefaults,
    keywords: ["dial", "switch", "stepped"],
  },
  {
    type: "core.toggle",
    label: "Toggle switch",
    category: "Controls",
    defaultSize: { width: 12, height: 18 },
    createDefaultProps: controlDefaults,
    keywords: ["switch", "lever", "stepped"],
  },
  {
    type: "core.lever",
    label: "Lever switch",
    category: "Controls",
    defaultSize: { width: 12, height: 18 },
    createDefaultProps: controlDefaults,
    keywords: ["switch", "toggle", "stepped"],
  },
  {
    type: "core.button",
    label: "Candy button",
    category: "Controls",
    defaultSize: { width: 18, height: 18 },
    createDefaultProps: controlDefaults,
    keywords: ["button", "illuminated", "momentary"],
  },
  {
    type: "core.slider",
    label: "Slider",
    category: "Controls",
    defaultSize: { width: 12, height: 48 },
    createDefaultProps: controlDefaults,
    keywords: ["fader", "linear", "control"],
  },
  {
    type: "core.wheel",
    label: "Performance wheel",
    category: "Controls",
    defaultSize: { width: 12, height: 42 },
    createDefaultProps: controlDefaults,
    keywords: ["pitch", "modulation", "performance"],
  },
  {
    type: "core.jack",
    label: "3.5 mm jack",
    category: "Patch points",
    defaultSize: { width: 9, height: 9 },
    createDefaultProps: () => ({ direction: "input", signalType: "unknown" }),
    keywords: ["socket", "patch", "eurorack"],
  },
  {
    type: "core.quarter-inch-jack",
    label: "1/4 inch jack",
    category: "Patch points",
    defaultSize: { width: 14, height: 14 },
    createDefaultProps: () => ({ direction: "input", signalType: "unknown" }),
    keywords: ["socket", "audio", "rear panel"],
  },
  {
    type: "core.led",
    label: "Indicator LED",
    category: "Indicators",
    defaultSize: { width: 4, height: 4 },
    createDefaultProps: () => ({ active: false, accent: "#ef5a47" }),
    keywords: ["light", "status", "indicator"],
  },
  {
    type: "core.panel",
    label: "Module panel",
    category: "Structure",
    defaultSize: { width: 50, height: 50 },
    createDefaultProps: () => ({
      fill: "#181c1c",
      border: "#c9cfcb",
      borderWidth: 0.5,
      radius: 1,
    }),
    keywords: ["module", "background", "container"],
  },
  {
    type: "core.logo",
    label: "Wordmark",
    category: "Branding",
    defaultSize: { width: 38, height: 10 },
    createDefaultProps: () => ({ text: "MOOG", colour: "#111617", outline: false }),
    keywords: ["logo", "brand", "text"],
  },
];

export function createCoreComponentRegistry() {
  const registry = new ComponentRegistry();
  registry.register(
    defineComponent({
      type: "core.knob",
      propertySchema: controlPropsSchema,
      compile: compileKnob,
    }),
  );
  registry.register(
    defineComponent({
      type: "core.trim-knob",
      propertySchema: controlPropsSchema,
      compile: compileKnob,
    }),
  );
  registry.register(
    defineComponent({
      type: "core.rotary-selector",
      propertySchema: controlPropsSchema,
      compile: compileSelector,
    }),
  );
  registry.register(
    defineComponent({
      type: "core.toggle",
      propertySchema: controlPropsSchema,
      compile: compileToggle,
    }),
  );
  registry.register(
    defineComponent({
      type: "core.lever",
      propertySchema: controlPropsSchema,
      compile: compileToggle,
    }),
  );
  registry.register(
    defineComponent({
      type: "core.button",
      propertySchema: controlPropsSchema,
      compile: compileButton,
    }),
  );
  registry.register(
    defineComponent({
      type: "core.slider",
      propertySchema: controlPropsSchema,
      compile: compileSlider,
    }),
  );
  registry.register(
    defineComponent({
      type: "core.wheel",
      propertySchema: controlPropsSchema,
      compile: compileWheel,
    }),
  );
  registry.register(
    defineComponent({ type: "core.jack", propertySchema: jackPropsSchema, compile: compileJack }),
  );
  registry.register(
    defineComponent({
      type: "core.quarter-inch-jack",
      propertySchema: jackPropsSchema,
      compile: compileJack,
    }),
  );
  registry.register(
    defineComponent({ type: "core.led", propertySchema: ledPropsSchema, compile: compileLed }),
  );
  registry.register(
    defineComponent({
      type: "core.panel",
      propertySchema: panelPropsSchema,
      compile: compilePanel,
    }),
  );
  registry.register(
    defineComponent({ type: "core.logo", propertySchema: logoPropsSchema, compile: compileLogo }),
  );
  return registry;
}
